import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';
import { ArrowLeft, ArrowRight, Compass, CheckCircle, ExternalLink, Mail, Sparkles, Trophy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DESTINOS = [
  { value: 'venezuela', label: 'Dentro de Venezuela', emoji: '🇻🇪' },
  { value: 'latam', label: 'Latinoamérica y Caribe', emoji: '🌎' },
  { value: 'usa_canada', label: 'Estados Unidos y Canadá', emoji: '🗽' },
  { value: 'europa', label: 'Europa / Schengen', emoji: '🇪🇺' },
  { value: 'resto', label: 'Resto del mundo', emoji: '🌏' },
];
const PRIORIDADES = [
  { value: 'precio', label: 'El mejor precio' },
  { value: 'medica', label: 'Cobertura médica alta' },
  { value: 'tarjeta_esim', label: 'Tarjeta recargable + eSIM' },
  { value: 'salas_vip', label: 'Salas VIP si se retrasa el vuelo' },
  { value: 'estudiante', label: 'Viaje de estudios' },
];

const Step = ({ n, current, label }) => (
  <div className={`flex items-center gap-2 text-sm font-medium ${n <= current ? 'text-white' : 'text-white/40'}`}>
    <span className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold ${n < current ? 'bg-emerald-400 text-slate-900' : n === current ? 'bg-white text-blue-600' : 'bg-white/10'}`}>
      {n < current ? '✓' : n}
    </span>
    <span className="hidden sm:inline">{label}</span>
  </div>
);

export const Comparador = ({ onTrackClick }) => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ destino: '', dias: 15, edad: 35, prioridades: [] });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lead, setLead] = useState({ nombre: '', email: '' });
  const [leadState, setLeadState] = useState('idle');

  const togglePrioridad = (v) => setForm((f) => ({
    ...f, prioridades: f.prioridades.includes(v) ? f.prioridades.filter((p) => p !== v) : [...f.prioridades, v],
  }));

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/comparador/recomendar`, form);
      setResult(data.data);
      setStep(4);
    } catch {
      toast({ title: 'Error', description: 'No pudimos calcular la recomendación. Intenta de nuevo.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const sendLead = async (e) => {
    e.preventDefault();
    setLeadState('sending');
    try {
      const { data } = await axios.post(`${API}/comparador/lead`, { ...form, ...lead, recomendado: result[0].nombre });
      setLeadState('done');
      toast({ title: '¡Listo!', description: data.message });
    } catch (err) {
      setLeadState('idle');
      const detail = err.response?.data?.detail;
      toast({ title: 'Error', description: typeof detail === 'string' ? detail : 'Revisa tu nombre y email.', variant: 'destructive' });
    }
  };

  const reset = () => { setStep(1); setResult(null); setLeadState('idle'); setLead({ nombre: '', email: '' }); };

  return (
    <section id="comparador" className="py-20 bg-slate-950 relative overflow-hidden" data-testid="comparador-section">
      <div className="absolute -top-32 left-1/4 w-[500px] h-[500px] rounded-full bg-blue-600/25 blur-3xl"></div>
      <div className="absolute -bottom-32 right-1/4 w-[500px] h-[500px] rounded-full bg-red-500/15 blur-3xl"></div>
      <div className="container mx-auto px-4 relative">
        <div className="max-w-2xl mb-10">
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-blue-300 bg-blue-500/10 border border-blue-500/20 rounded-full px-4 py-1.5 mb-4">
            <Compass className="h-4 w-4" /> Comparador rápido
          </span>
          <h3 className="text-4xl font-bold text-white mb-3">¿Cuál aliado te conviene cotizar primero?</h3>
          <p className="text-lg text-slate-400">Responde 3 preguntas y te decimos con quién empezar según tu viaje.</p>
        </div>

        <div className="max-w-3xl bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-6 sm:p-10">
          {step < 4 && (
            <div className="flex items-center justify-between mb-8">
              <Step n={1} current={step} label="Destino" />
              <div className="flex-1 h-px bg-white/10 mx-3"></div>
              <Step n={2} current={step} label="Viaje" />
              <div className="flex-1 h-px bg-white/10 mx-3"></div>
              <Step n={3} current={step} label="Prioridades" />
            </div>
          )}

          {step === 1 && (
            <div data-testid="comparador-step-destino">
              <h4 className="text-xl font-semibold text-white mb-5">¿A dónde viajas?</h4>
              <div className="grid sm:grid-cols-2 gap-3">
                {DESTINOS.map((d) => (
                  <button key={d.value} type="button" data-testid={`comparador-destino-${d.value}`} onClick={() => setForm((f) => ({ ...f, destino: d.value }))}
                    className={`flex items-center gap-3 text-left rounded-2xl border px-4 py-4 transition-[background-color,border-color,transform] duration-200 hover:scale-[1.02] ${form.destino === d.value ? 'bg-blue-600 border-blue-400 text-white' : 'bg-white/5 border-white/10 text-slate-200 hover:bg-white/10'}`}>
                    <span className="text-2xl">{d.emoji}</span>
                    <span className="font-medium">{d.label}</span>
                  </button>
                ))}
              </div>
              <div className="flex justify-end mt-8">
                <Button data-testid="comparador-next-1" disabled={!form.destino} onClick={() => setStep(2)} className="rounded-full bg-white text-blue-600 hover:bg-white/90 px-6">
                  Siguiente <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="comparador-step-viaje">
              <h4 className="text-xl font-semibold text-white mb-5">Cuéntanos de tu viaje</h4>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm text-slate-300 mb-2">Días de viaje: <span className="text-white font-bold" data-testid="comparador-dias-value">{form.dias}</span></label>
                  <input type="range" min="1" max="365" value={form.dias} data-testid="comparador-dias-range" onChange={(e) => setForm((f) => ({ ...f, dias: Number(e.target.value) }))} className="w-full accent-blue-500" />
                  <div className="flex justify-between text-xs text-slate-500 mt-1"><span>1 día</span><span>365 días</span></div>
                </div>
                <div>
                  <label className="block text-sm text-slate-300 mb-2">Edad del viajero</label>
                  <Input type="number" min="0" max="99" value={form.edad} data-testid="comparador-edad-input" onChange={(e) => setForm((f) => ({ ...f, edad: Math.min(99, Math.max(0, Number(e.target.value) || 0)) }))} className="h-12 bg-slate-900/70 border-white/10 text-white rounded-xl text-lg" />
                </div>
              </div>
              <div className="flex justify-between mt-8">
                <Button variant="ghost" data-testid="comparador-back-2" onClick={() => setStep(1)} className="rounded-full text-slate-300 hover:text-white hover:bg-white/10"><ArrowLeft className="h-4 w-4 mr-2" /> Atrás</Button>
                <Button data-testid="comparador-next-2" onClick={() => setStep(3)} className="rounded-full bg-white text-blue-600 hover:bg-white/90 px-6">Siguiente <ArrowRight className="h-4 w-4 ml-2" /></Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="comparador-step-prioridades">
              <h4 className="text-xl font-semibold text-white mb-1">¿Qué es lo más importante para ti?</h4>
              <p className="text-sm text-slate-400 mb-5">Elige una o varias (opcional).</p>
              <div className="flex flex-wrap gap-3">
                {PRIORIDADES.map((p) => (
                  <button key={p.value} type="button" data-testid={`comparador-prioridad-${p.value}`} onClick={() => togglePrioridad(p.value)}
                    className={`rounded-full border px-4 py-2.5 text-sm font-medium transition-colors ${form.prioridades.includes(p.value) ? 'bg-emerald-400 border-emerald-300 text-slate-900' : 'bg-white/5 border-white/10 text-slate-200 hover:bg-white/10'}`}>
                    {form.prioridades.includes(p.value) && <CheckCircle className="inline h-4 w-4 mr-1.5 -mt-0.5" />}{p.label}
                  </button>
                ))}
              </div>
              <div className="flex justify-between mt-8">
                <Button variant="ghost" data-testid="comparador-back-3" onClick={() => setStep(2)} className="rounded-full text-slate-300 hover:text-white hover:bg-white/10"><ArrowLeft className="h-4 w-4 mr-2" /> Atrás</Button>
                <Button data-testid="comparador-submit" disabled={loading} onClick={submit} className="rounded-full bg-gradient-to-r from-blue-600 to-red-500 text-white hover:opacity-90 px-6">
                  <Sparkles className="h-4 w-4 mr-2" /> {loading ? 'Calculando...' : 'Ver recomendación'}
                </Button>
              </div>
            </div>
          )}

          {step === 4 && result && (
            <div data-testid="comparador-result">
              <div className="flex items-center gap-2 text-emerald-300 text-sm font-semibold mb-3"><Trophy className="h-4 w-4" /> Nuestra recomendación para tu viaje</div>
              <div className={`rounded-3xl p-6 bg-gradient-to-br ${result[0].color} text-white shadow-2xl`} data-testid="comparador-top">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                  <div className="flex items-center gap-4">
                    <div className="bg-white rounded-2xl p-3 shadow-lg"><img src={result[0].logo} alt={result[0].nombre} className="h-10 w-auto max-w-[140px] object-contain" /></div>
                    <div>
                      <p className="text-xs uppercase tracking-widest opacity-80">Cotiza primero con</p>
                      <h5 className="text-2xl font-bold" data-testid="comparador-top-name">{result[0].nombre}</h5>
                    </div>
                  </div>
                  <Button data-testid="comparador-top-quote-btn" onClick={() => { onTrackClick(result[0].nombre); window.open(result[0].link, '_blank'); }} className="rounded-full bg-white text-slate-900 hover:bg-white/90 font-semibold px-6">
                    Cotizar ahora <ExternalLink className="h-4 w-4 ml-2" />
                  </Button>
                </div>
                <ul className="grid sm:grid-cols-2 gap-2 mt-5 text-sm">
                  {result[0].razones.map((r) => <li key={r} className="flex items-start gap-2"><CheckCircle className="h-4 w-4 mt-0.5 shrink-0" />{r}</li>)}
                </ul>
              </div>

              {result.length > 1 && (
                <div className="mt-6">
                  <p className="text-sm text-slate-400 mb-3">Otras opciones para tu viaje</p>
                  <div className="grid sm:grid-cols-3 gap-3">
                    {result.slice(1).map((r, i) => (
                      <button key={r.nombre} type="button" data-testid={`comparador-alt-${i}`} onClick={() => { onTrackClick(r.nombre); window.open(r.link, '_blank'); }}
                        className="text-left rounded-2xl bg-white/5 border border-white/10 p-4 hover:bg-white/10 transition-colors">
                        <p className="font-semibold text-white text-sm">{r.nombre}</p>
                        <p className="text-xs text-slate-400 mt-1 line-clamp-2">{r.features[0]}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-8 rounded-2xl border border-white/10 bg-slate-900/60 p-5" data-testid="comparador-lead-box">
                {leadState === 'done' ? (
                  <p className="flex items-center gap-2 text-emerald-300 font-medium" data-testid="comparador-lead-success"><CheckCircle className="h-5 w-5" /> Te enviamos la recomendación y tu código WELCOME10 por email.</p>
                ) : (
                  <form onSubmit={sendLead} className="flex flex-col sm:flex-row gap-3">
                    <div className="flex items-center gap-2 text-slate-300 text-sm sm:w-56 shrink-0"><Mail className="h-4 w-4 text-blue-400" /> Recibe esta recomendación y un 10% de descuento</div>
                    <Input data-testid="comparador-lead-nombre" required minLength={2} placeholder="Tu nombre" value={lead.nombre} onChange={(e) => setLead((l) => ({ ...l, nombre: e.target.value }))} className="h-11 bg-slate-950 border-white/10 text-white rounded-xl" />
                    <Input data-testid="comparador-lead-email" required type="email" placeholder="tu@email.com" value={lead.email} onChange={(e) => setLead((l) => ({ ...l, email: e.target.value }))} className="h-11 bg-slate-950 border-white/10 text-white rounded-xl" />
                    <Button type="submit" data-testid="comparador-lead-submit" disabled={leadState === 'sending'} className="h-11 rounded-xl bg-blue-600 hover:bg-blue-500 shrink-0">{leadState === 'sending' ? 'Enviando...' : 'Enviar'}</Button>
                  </form>
                )}
              </div>

              <button type="button" data-testid="comparador-reset" onClick={reset} className="mt-6 text-sm text-slate-400 hover:text-white underline underline-offset-4">Volver a empezar</button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Comparador;
